import { takeLatest, put, all, call, CallEffect, PutEffect } from 'redux-saga/effects';
import LeaveReasonMasterActionTypes from './LeaveReasonMaster.types';
import {
	getLeaveReasonMasterData,
	updateLeaveReasonMasterData as updateLeaveReasonMasterService,
	addLeaveReasonMasterData as addLeaveReasonMasterService,
} from './LeaveReasonMaster.services';
import {
	leaveReasonMasterSuccess,
	leaveReasonMasterFailure,
	updateLeaveReasonMasterSuccess,
	updateLeaveReasonMasterFailure,
	addLeaveReasonMasterSuccess,
	addLeaveReasonMasterFailure,
} from './LeaveReasonMaster.action';
import AlertManager from '../../utils/alertManager';
import { encryptData, decryptData } from '../_common/enode-decode';

const decryptLeaveReasonResponse = (responseData: any, key: string) => {
	const baseData = responseData?.data;
	if (baseData?.response) {
		const decrypted = decryptData(baseData.response, key);
		if (decrypted) {
			return {
				...decrypted,
				status: baseData.status ?? decrypted.status,
				message_text: baseData.message_text ?? decrypted.message_text,
			};
		}
	}
	return baseData;
};

export function* fetchLeaveReasonMasterData(): Generator<CallEffect | PutEffect, void, any> {
	try {
		const encryptedPayload = encryptData(
			{},
			LeaveReasonMasterActionTypes.LEAVE_REASON_MASTER_KEY,
		);
		const response = yield call(getLeaveReasonMasterData, encryptedPayload);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			const parsedPayload = decryptLeaveReasonResponse(
				response,
				LeaveReasonMasterActionTypes.LEAVE_REASON_MASTER_KEY,
			);
			if (response.data.status === 'success') {
				yield put(leaveReasonMasterSuccess(parsedPayload));
			} else {
				yield put(leaveReasonMasterFailure(parsedPayload));
			}
		}
	} catch (error) {
		yield put(leaveReasonMasterFailure(error));
	}
}

export function* updateLeaveReasonMasterData(
	data: any,
): Generator<CallEffect | PutEffect, void, any> {
	try {
		const encryptedPayload = encryptData(
			data,
			LeaveReasonMasterActionTypes.UPDATE_LEAVE_REASON_MASTER_KEY,
		);
		const response = yield call(updateLeaveReasonMasterService, encryptedPayload);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			const parsedPayload = decryptLeaveReasonResponse(
				response,
				LeaveReasonMasterActionTypes.UPDATE_LEAVE_REASON_MASTER_KEY,
			);
			if (response.data.status === 'success') {
				yield put(updateLeaveReasonMasterSuccess(parsedPayload));
			} else {
				yield put(updateLeaveReasonMasterFailure(parsedPayload));
			}
		}
	} catch (error) {
		yield put(updateLeaveReasonMasterFailure(error));
	}
}

export function* addLeaveReasonMasterData(data: any): Generator<CallEffect | PutEffect, void, any> {
	try {
		const encryptedPayload = encryptData(
			data,
			LeaveReasonMasterActionTypes.ADD_LEAVE_REASON_MASTER_KEY,
		);
		const response = yield call(addLeaveReasonMasterService, encryptedPayload);
		if (response.data.statusCode === '401') {
			// Show token validation alert
			AlertManager.showTokenValidationAlert();
			return;
		} else {
			const parsedPayload = decryptLeaveReasonResponse(
				response,
				LeaveReasonMasterActionTypes.ADD_LEAVE_REASON_MASTER_KEY,
			);
			if (response.data.status === 'success') {
				yield put(addLeaveReasonMasterSuccess(parsedPayload));
			} else {
				yield put(addLeaveReasonMasterFailure(parsedPayload));
			}
		}
	} catch (error) {
		yield put(addLeaveReasonMasterFailure(error));
	}
}

export function* onLeaveReasonMasterSagaStart() {
	yield takeLatest(
		LeaveReasonMasterActionTypes.LEAVE_REASON_MASTER_START,
		fetchLeaveReasonMasterData,
	);
}

export function* onUpdateLeaveReasonMasterSagaStart() {
	yield takeLatest(
		LeaveReasonMasterActionTypes.UPDATE_LEAVE_REASON_MASTER_START,
		updateLeaveReasonMasterData,
	);
}

export function* onAddLeaveReasonMasterSagaStart() {
	yield takeLatest(
		LeaveReasonMasterActionTypes.ADD_LEAVE_REASON_MASTER_START,
		addLeaveReasonMasterData,
	);
}

export function* leaveReasonMasterSaga() {
	yield all([
		call(onLeaveReasonMasterSagaStart),
		call(onUpdateLeaveReasonMasterSagaStart),
		call(onAddLeaveReasonMasterSagaStart),
	]);
}
